import { Store } from '../store'; // eslint-disable-line no-unused-vars
import { makeAutoObservable, runInAction } from 'mobx';
import { API_ENDPOINTS } from '../../api/endpoints';
import {
  ADMIN_DASHBOARD_TABS,
  ADMIN_DASHBOARD_USER_GROUPS_ARRAY_SELECTABLE
} from '../../pages/AdminDashboard/constants';
import { mapData } from '../../api/dataMappers';
import {
  FUND_FIELDS,
  PORTFOLIO_FIELDS,
  INVEST_PROFILE_FIELDS,
  COMPANY_PROFILE_FIELDS,
  USER_PROFILE_FIELDS
} from '../../utils/constants/fields';
import { PUNCTUATION } from '../../utils/constants/notation';

const getInitialFiltersDisplayUsersManagement = () => {
  return JSON.parse(
    JSON.stringify({
      filterCompanyType: '',
      filterConfirmationStatus: false,
      filterApproveStatus: '',
      filterOnlyMasterGroup: false
    })
  );
};

const getInitialFiltersDisplayUsersActivities = () => {
  return JSON.parse(
    JSON.stringify({
      filterCompanyType: '',
      filterOnlyMasterGroup: false
    })
  );
};

class AdminDashboardStore {
  /**
   * @param {Store} root
   */
  constructor(root) {
    makeAutoObservable(this);
    this.root = root;
  }

  get pageSize() {
    return +(import.meta.env.VITE_PAGE_SIZE || 50);
  }

  sortUsersManagement = '';
  sortUsersActivities = '';

  reset = () => {
    this.tab = ADMIN_DASHBOARD_TABS.USER_MANAGEMENT;

    this.usersManagementList = [];
    this.loadUsersManagementError = null;
    this.isLoadingUsersManagement = false;
    this.filtersDisplayUsersManagement = getInitialFiltersDisplayUsersManagement();
    this.filtersUsersManagement = getInitialFiltersDisplayUsersManagement();
    this.usersManagementPage = 1;
    this.usersManagementMaxPage = 1;
    this.usersManagementTotalCount = 0;
    this.isRefreshingUsersManagementUser = false;
    this.refreshUsersManagementUserError = null;
    this.sortUsersManagement = '';
    this.sortUsersActivities = '';

    this.usersActivitiesList = [];
    this.loadUsersActivitiesError = null;
    this.isLoadingUsersActivities = false;
    this.filtersDisplayUsersActivities = getInitialFiltersDisplayUsersActivities();
    this.filtersUsersActivities = getInitialFiltersDisplayUsersActivities();
    this.usersActivitiesPage = 1;
    this.usersActivitiesMaxPage = 1;
    this.usersActivitiesTotalCount = 0;

    this.investVehiclesList = [];
    this.loadInvestVehiclesError = null;
    this.isLoadingInvestVehicles = false;
    this.investVehiclesFilterType = '';
    this.investVehiclesPage = 1;
    this.investVehiclesMaxPage = 1;
    this.investVehiclesTotalCount = 0;

    this.isUpdatingUser = false;
  };

  tab = ADMIN_DASHBOARD_TABS.USER_MANAGEMENT;

  usersManagementList = [];
  loadUsersManagementError = null;
  isLoadingUsersManagement = false;
  filtersDisplayUsersManagement = getInitialFiltersDisplayUsersManagement();
  filtersUsersManagement = getInitialFiltersDisplayUsersManagement();
  usersManagementPage = 1;
  usersManagementMaxPage = 1;
  usersManagementTotalCount = 0;
  isRefreshingUsersManagementUser = false;
  refreshUsersManagementUserError = null;

  usersActivitiesList = [];
  loadUsersActivitiesError = null;
  isLoadingUsersActivities = false;
  filtersDisplayUsersActivities = getInitialFiltersDisplayUsersActivities();
  filtersUsersActivities = getInitialFiltersDisplayUsersActivities();
  usersActivitiesPage = 1;
  usersActivitiesMaxPage = 1;
  usersActivitiesTotalCount = 0;

  investVehiclesList = [];
  loadInvestVehiclesError = null;
  isLoadingInvestVehicles = false;
  investVehiclesFilterType = '';
  investVehiclesPage = 1;
  investVehiclesMaxPage = 1;
  investVehiclesTotalCount = 0;

  isUpdatingUser = false;

  // Reads the APPLIED filters (`filtersUsersManagement`), never the draft ones bound
  // to the filter drawer. loadUsersManagementList snapshots display -> applied before
  // calling this; refreshUsersManagementUser deliberately does not, so a background
  // refresh can't silently commit filter edits the admin hasn't submitted yet.
  buildUsersManagementRequestBody = (page = 1) => {
    const filters = this.filtersUsersManagement;
    const body = { page: page - 1, pageSize: this.pageSize };
    if (filters.filterCompanyType && filters.filterCompanyType !== 'all') {
      body.filterCompanyType = filters.filterCompanyType;
    }
    if (filters.filterApproveStatus) {
      body.filterApproveStatus = filters.filterApproveStatus;
    }
    if (filters.filterConfirmationStatus) {
      body.filterConfirmationStatus = true;
    }
    if (filters.filterOnlyMasterGroup) {
      body.filterOnlyMasterGroup = true;
    }

    if (this.sortUsersManagement) {
      body.sortBy = this.sortUsersManagement;
    }

    return body;
  };

  loadUsersManagementList = (page = 1) => {
    if (this.isLoadingUsersManagement) {
      return;
    }

    runInAction(() => {
      this.usersManagementList = [];
      this.isLoadingUsersManagement = true;
      this.filtersUsersManagement = JSON.parse(JSON.stringify(this.filtersDisplayUsersManagement));
      this.usersManagementPage = page;
      this.usersManagementMaxPage = 1;
      this.loadUsersManagementError = null;
      this.usersManagementTotalCount = 0;
    });

    this.root.makeRequest({
      endpoint: API_ENDPOINTS.ADMIN_LIST_USERS,
      body: this.buildUsersManagementRequestBody(page),
      onSuccess: (response) => {
        this.usersManagementList = response.users;
        this.usersManagementMaxPage = response.maxPage;
        this.usersManagementTotalCount = response.totalCount;
      },
      onError: (errorMessage) => {
        this.loadUsersManagementError = errorMessage || 'Failed to obtain users management list.';
      },
      onFinally: () => {
        this.isLoadingUsersManagement = false;
      }
    });
  };

  loadUsersActivitiesList = (page = 1) => {
    if (this.isLoadingUsersActivities) {
      return;
    }

    runInAction(() => {
      this.usersActivitiesList = [];
      this.isLoadingUsersActivities = true;
      this.filtersUsersActivities = JSON.parse(JSON.stringify(this.filtersDisplayUsersActivities));
      this.usersActivitiesPage = page;
      this.usersActivitiesMaxPage = 1;
      this.loadUsersActivitiesError = null;
      this.usersActivitiesTotalCount = 0;
    });

    const filters = this.filtersUsersActivities;
    const body = { page: page - 1, pageSize: this.pageSize };
    if (filters.filterCompanyType && filters.filterCompanyType !== 'all') {
      body.filterCompanyType = filters.filterCompanyType;
    }
    if (filters.filterOnlyMasterGroup) {
      body.filterOnlyMasterGroup = true;
    }
    if (this.sortUsersActivities) {
      body.sortBy = this.sortUsersActivities;
    }
    this.root.makeRequest({
      endpoint: API_ENDPOINTS.ADMIN_LIST_USERS,
      body,
      onSuccess: (response) => {
        this.usersActivitiesList = response.users;
        this.usersActivitiesMaxPage = response.maxPage;
        this.usersActivitiesTotalCount = response.totalCount;
      },
      onError: (errorMessage) => {
        this.loadUsersActivitiesError = errorMessage || 'Failed to obtain users activities list.';
      },
      onFinally: () => {
        this.isLoadingUsersActivities = false;
      }
    });
  };

  loadInvestVehiclesList = (page = 1) => {
    if (this.isLoadingInvestVehicles) {
      return;
    }

    runInAction(() => {
      this.investVehiclesList = [];
      this.isLoadingInvestVehicles = true;
      this.investVehiclesPage = page;
      this.investVehiclesMaxPage = 1;
      this.loadInvestVehiclesError = null;
      this.investVehiclesTotalCount = 0;
    });

    const body = { page: page - 1, pageSize: this.pageSize };
    if (this.investVehiclesFilterType && this.investVehiclesFilterType !== 'all') {
      body.filterType = this.investVehiclesFilterType;
    }

    this.root.makeRequest({
      endpoint: API_ENDPOINTS.ADMIN_LIST_FPI,
      body,
      onSuccess: (response) => {
        const mapperFields = {
          fund: FUND_FIELDS,
          portfolioCompany: PORTFOLIO_FIELDS,
          investProfile: INVEST_PROFILE_FIELDS
        };

        this.investVehiclesList = response.fpis.map((entry) => {
          const parsedEntry = mapData(entry, mapperFields[entry.entryType] || mapperFields.fund);
          parsedEntry.companyProfile = mapData(entry.companyProfile || {}, COMPANY_PROFILE_FIELDS);
          parsedEntry.user = mapData(entry.user || {}, USER_PROFILE_FIELDS);
          parsedEntry.contact = {};
          parsedEntry.contact.userName =
            (
              parsedEntry.user[USER_PROFILE_FIELDS.FIRST_NAME.NAME] +
              ' ' +
              parsedEntry.user[USER_PROFILE_FIELDS.LAST_NAME.NAME]
            ).trim() || PUNCTUATION.EMPTY_VALUE;
          parsedEntry.contact.companyName =
            parsedEntry.companyProfile[COMPANY_PROFILE_FIELDS.COMPANY_NAME.NAME] ||
            parsedEntry.companyName ||
            PUNCTUATION.EMPTY_VALUE;

          const [userCity, userCountry] = [
            parsedEntry.user[USER_PROFILE_FIELDS.CITY.NAME],
            this.root.utilsStore.getOptionName(parsedEntry.user, USER_PROFILE_FIELDS.COUNTRY)?.[0]
          ];
          const [companyCity, companyCountry] = [
            parsedEntry.companyProfile[COMPANY_PROFILE_FIELDS.CITY.NAME],
            this.root.utilsStore.getOptionName(
              parsedEntry.companyProfile,
              COMPANY_PROFILE_FIELDS.COUNTRY
            )?.[0]
          ];

          if (userCity || userCountry) {
            parsedEntry.contact.location = userCity;
            if (userCountry) {
              parsedEntry.contact.location = parsedEntry.contact.location
                ? `${parsedEntry.contact.location}, ${userCountry}`
                : userCountry;
            }
          } else {
            parsedEntry.contact.location = companyCity;
            if (companyCountry) {
              parsedEntry.contact.location = parsedEntry.contact.location
                ? `${parsedEntry.contact.location}, ${companyCountry}`
                : companyCountry;
            }
          }

          if (!parsedEntry.contact.location) {
            parsedEntry.contact.location = PUNCTUATION.EMPTY_VALUE;
          }

          const [userPhone, companyPhone] = [
            parsedEntry.user[USER_PROFILE_FIELDS.PHONE_NUMBER.NAME],
            parsedEntry.companyProfile[COMPANY_PROFILE_FIELDS.PHONE_NUMBER.NAME]
          ];

          if (userPhone) {
            parsedEntry.contact.phone = userPhone;
          } else {
            parsedEntry.contact.phone = companyPhone;
          }

          if (!parsedEntry.contact.phone) {
            parsedEntry.contact.phone = PUNCTUATION.EMPTY_VALUE;
          }

          const [userEmail, companyEmail] = [
            parsedEntry.user[USER_PROFILE_FIELDS.EMAIL.NAME],
            parsedEntry.companyProfile[COMPANY_PROFILE_FIELDS.CONTACT_EMAIL.NAME]
          ];

          if (userEmail) {
            parsedEntry.contact.email = userEmail;
          } else {
            parsedEntry.contact.email = companyEmail;
          }

          if (!parsedEntry.contact.email) {
            parsedEntry.contact.email = PUNCTUATION.EMPTY_VALUE;
          }

          return parsedEntry;
        });

        this.investVehiclesMaxPage = response.maxPage;
        this.investVehiclesTotalCount = response.totalCount;
      },
      onError: (errorMessage) => {
        this.loadInvestVehiclesError = errorMessage || 'Failed to obtain invest profiles list.';
      },
      onFinally: () => {
        this.isLoadingInvestVehicles = false;
      }
    });
  };

  clearUsersManagementFilters = () => {
    runInAction(() => {
      this.filtersDisplayUsersManagement = getInitialFiltersDisplayUsersManagement();
    });
    if (
      JSON.stringify(this.filtersDisplayUsersManagement) !==
      JSON.stringify(this.filtersUsersManagement)
    ) {
      this.loadUsersManagementList();
    }
  };

  clearUsersActivitiesFilters = () => {
    runInAction(() => {
      this.filtersDisplayUsersActivities = getInitialFiltersDisplayUsersActivities();
    });
    if (
      JSON.stringify(this.filtersDisplayUsersActivities) !==
      JSON.stringify(this.filtersUsersActivities)
    ) {
      this.loadUsersActivitiesList();
    }
  };

  updateUserGroups = (
    user = {},
    cognitoGroups = [],
    onSuccess = () => {},
    onError = () => {},
    onFinally = () => {}
  ) => {
    if (this.isUpdatingUser) {
      return;
    }

    runInAction(() => {
      this.isUpdatingUser = true;
    });

    this.root.makeRequest({
      endpoint: API_ENDPOINTS.ADMIN_UPDATE_USER,
      body: { id: user.id, cognitoGroups },
      onSuccess: (response) => {
        // The optimistic patch: what we expect the group set to be, derived from
        // the set we submitted. Retained as the fallback for backends that don't
        // return the resulting groups yet — see the read of `response` below.
        let newGroups = user.cognitoGroups.slice();
        ADMIN_DASHBOARD_USER_GROUPS_ARRAY_SELECTABLE.forEach((group) => {
          if (cognitoGroups.includes(group.value) && !newGroups.includes(group.value)) {
            newGroups.push(group.value);
          } else if (!cognitoGroups.includes(group.value) && newGroups.includes(group.value)) {
            newGroups = newGroups.filter((g) => g !== group.value);
          }
        });
        // Prefer the groups the backend actually applied over the optimistic
        // patch, so the row reflects truth rather than the set we submitted.
        // Read `response.cognitoGroups`, not `response` itself: the envelope is
        // unwrapped to `data` upstream, so this is an OBJECT, and assigning it
        // raw would leave user.cognitoGroups a non-array — which every consumer
        // (.includes/.map/.length/.slice) then blows up on.
        // `??` not `||`: an empty array is a legitimate result (a user stripped
        // of every group) and must not silently fall back to the patch.
        user.cognitoGroups = response?.cognitoGroups ?? newGroups;

        onSuccess(response);
      },
      onError: (errorMessage) => {
        onError(errorMessage);
      },
      onFinally: () => {
        this.isUpdatingUser = false;
        onFinally();
      }
    });
  };

  /**
   * TD-2361: re-read the current page and fold the result into the rows we already
   * hold, so an editor opened on a cached row can seed itself from live state.
   *
   * Deliberately NOT loadUsersManagementList(page): that blanks usersManagementList
   * synchronously, and the group popup is mounted off a `.find()` into that list —
   * blanking it makes the lookup undefined and unmounts the popup as it opens.
   *
   * @param {string} userId the row the caller cares about; drives onMissing only
   * @param {{ onMissing?: () => void }} [callbacks] onMissing fires when that row is
   *   absent from the refreshed page (deleted elsewhere, or moved out by a filter)
   */
  refreshUsersManagementUser = (userId, { onMissing = () => {} } = {}) => {
    // A full list load is authoritative and about to overwrite everything anyway,
    // so don't race it.
    if (!userId || this.isRefreshingUsersManagementUser || this.isLoadingUsersManagement) {
      return;
    }

    runInAction(() => {
      this.isRefreshingUsersManagementUser = true;
      this.refreshUsersManagementUserError = null;
    });

    this.root.makeRequest({
      endpoint: API_ENDPOINTS.ADMIN_LIST_USERS,
      body: this.buildUsersManagementRequestBody(this.usersManagementPage),
      onSuccess: (response) => {
        const freshById = new Map((response?.users || []).map((u) => [u.id, u]));

        // Object.assign onto the EXISTING row rather than replacing it. Consumers
        // capture the row by reference — SelectUserGroupsPopup's useLocalObservable
        // initialiser closes over the `user` prop from its first render, and every
        // getter plus the updateUserGroups call reads through that reference. Swap
        // the object out and the popup is left editing an orphan detached from the
        // list. Mutating keeps those references live, and the fresh cognitoGroups
        // array is a new identity, which re-fires the popup's seeding effect.
        runInAction(() => {
          this.usersManagementList.forEach((row) => {
            const fresh = freshById.get(row.id);
            if (fresh) {
              Object.assign(row, fresh);
            }
          });
        });

        // Rows the response added are ignored on purpose: inserting or dropping rows
        // under an open editor is worse than showing a slightly short page until the
        // next explicit load.
        if (!freshById.has(userId)) {
          onMissing();
        }
      },
      onError: (errorMessage) => {
        this.refreshUsersManagementUserError =
          errorMessage || 'Failed to read the current user access.';
      },
      onFinally: () => {
        this.isRefreshingUsersManagementUser = false;
      }
    });
  };

  /**
   * The error is store-scoped, so an editor that unmounts on a failed read has to
   * clear it on the way out — otherwise the next editor paints the stale banner on
   * its first frame, before its own read has even started.
   */
  clearRefreshUsersManagementUserError = () => {
    runInAction(() => {
      this.refreshUsersManagementUserError = null;
    });
  };

  clearUsersManagementSort = () => {
    runInAction(() => {
      this.sortUsersManagement = '';
    });
    this.loadUsersManagementList();
  };
}

export default AdminDashboardStore;
